/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Fragment, useEffect } from 'react'

import { EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AsyncRequestState, EsProxyResponseConsole, StackDeployment } from '@modules/ui-types'
import { isNonEmptyArray } from '@modules/ts-utils'
import { DocLink } from '@modules/doclinks'
import { ConsolePrivacyWarning } from '@modules/one-console/ConsolePrivacyWarning'
import Feature from '@modules/utils/feature'

import { isFeatureActivated } from '@/store'
import {
  getPlanInfo,
  getFirstEsClusterFromGet,
} from '@/lib/stackDeployments/selectors/fundamentals'

import Request from './Request'
import Output from './Output'

import type { ConsoleRequestState } from '../../reducers/apiConsole'
import type { FC } from 'react'

type Props = {
  deployment: StackDeployment | null
  consoleRequest?: ConsoleRequestState | null
  consoleRequestHistory: ConsoleRequestState[]
  consoleResponse: EsProxyResponseConsole | null
  queryDeploymentProxyRequest: AsyncRequestState
  clearAPIConsoleHistory: () => void
  clearDeploymentProxyResponse: (deploymentId: string) => void
  queryDeploymentProxyForConsole: (
    deployment: StackDeployment,
    consoleRequest: ConsoleRequestState,
  ) => void
  setAPIConsoleRequest: (request: ConsoleRequestState, deployment: StackDeployment) => void
}

const APIConsole: FC<Props> = ({
  deployment,
  consoleRequest,
  consoleRequestHistory,
  consoleResponse,
  queryDeploymentProxyRequest,
  clearAPIConsoleHistory,
  clearDeploymentProxyResponse,
  queryDeploymentProxyForConsole,
  setAPIConsoleRequest,
}) => {
  const getInitialRequest = (): ConsoleRequestState => {
    if (isNonEmptyArray(consoleRequestHistory)) {
      return consoleRequestHistory[0]
    }

    return {
      method: `GET`,
      path: `/_search`,
      body: ``,
      advancedMode: false,
      filterBy: `regex`,
      filterRegex: ``,
      filterJq: ``,
      invertFilter: false,
    }
  }

  useEffect(() => {
    if (deployment) {
      setAPIConsoleRequest(getInitialRequest(), deployment)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showSupportInstructions = isFeatureActivated(Feature.showSupportInstructions)

  if (!deployment) {
    return null
  }

  const firstEs = getFirstEsClusterFromGet({ deployment })

  if (firstEs && !getPlanInfo({ resource: firstEs })) {
    return null
  }

  if (consoleRequest == null) {
    return null
  }

  const apiConsoleStyle = css({
    '.ReactCodeMirror': {
      maxWidth: 'initial',
    },
  })

  return (
    <Fragment>
      {showSupportInstructions && (
        <Fragment>
          <ConsolePrivacyWarning />
          <EuiSpacer size='m' />
        </Fragment>
      )}
      <div css={apiConsoleStyle}>
        <EuiText>
          <FormattedMessage
            id='api-console.overview'
            defaultMessage='Perform operations-related tasks from this console. You can run search queries, review the list of snapshots, check the health of your clusters, and { link }.'
            values={{
              link: (
                <DocLink link='apiConsoleDocLink'>
                  <FormattedMessage id='api-console.overview.more' defaultMessage='more' />
                </DocLink>
              ),
            }}
          />
        </EuiText>

        <EuiSpacer size='m' />

        <Request
          deployment={deployment}
          request={consoleRequest}
          requestHistory={consoleRequestHistory}
          clearDeploymentProxyResponse={clearDeploymentProxyResponse}
          clearAPIConsoleHistory={clearAPIConsoleHistory}
          onChange={setAPIConsoleRequest}
          inProgress={queryDeploymentProxyRequest.inProgress}
          submitQueryToDeploymentProxy={(request) => {
            queryDeploymentProxyForConsole(deployment, request)
          }}
        />

        <EuiSpacer size='m' />

        <Output
          requestSettings={consoleRequest}
          request={queryDeploymentProxyRequest}
          response={consoleResponse}
        />
      </div>
    </Fragment>
  )
}

export default APIConsole
